import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

export type NoticeTone = 'info' | 'success' | 'danger';

const TONE_CLASSES: Record<NoticeTone, string> = {
  info: 'border-border bg-muted/60 text-foreground',
  success: 'border-emerald-600/30 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
  danger: 'border-destructive/40 bg-destructive/5 text-destructive',
};

export interface NoticeProps {
  tone?: NoticeTone;
  title?: string;
  children?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

/** An in-page message (the app's toast equivalent): saved, failed, or the one-time temporary password. */
export const Notice = ({ tone = 'info', title, children, onDismiss, className }: NoticeProps) => {
  const { t } = useI18nTranslation();

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-md border px-4 py-3 text-sm', TONE_CLASSES[tone], className)}
    >
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('actions.dismiss')}
          className="rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
};
